import { GF_FieldError, GF_Select } from '../../../model/gforms/gform';
import useGravityForm, {
  ACTION_TYPES,
  FieldValue,
  StringFieldValue,
} from '../../../hooks/GFormsHooks';
import { useEffect } from 'react';
import { IonItem, IonList, IonSelect, IonSelectOption } from '@ionic/react';

import './SelectField.css';
import { useIntl } from 'react-intl';
import { FieldDescription } from './subparts/FieldDescription';
import { FieldDecorator } from './subparts/FieldDecorator';

interface Props {
  field: GF_Select;
  fieldErrors: GF_FieldError[];
}

export default function SelectField({ field, fieldErrors }: Props) {
  const intl = useIntl();

  const {
    id,
    formId,
    type,
    label,
    placeholder,
    description,
    cssClass,
    isRequired,
    choices,
    visibility,
  } = field;
  const htmlId = `field_${formId}_${id}`;
  const { state, dispatch } = useGravityForm();
  const fieldValue = state.find(
    (fieldValue: FieldValue) => fieldValue.id === id
  ) as StringFieldValue | undefined;
  const defaultValue =
    choices?.find((choice) => choice.isSelected === true)?.value || '';
  const value = fieldValue?.value || defaultValue;
  const options = choices?.filter((c) => c.value || !isRequired);

  const customActionSheetOptions = {
    header: label,
    className: 'SelectFieldActionSheet',
    mode: 'ios',
    buttons: [
      {
        text: intl.formatMessage({ id: 'Cancel' }),
        role: 'cancel',
        data: {
          action: 'cancel',
        },
      },
    ],
  };

  useEffect(() => {
    if (defaultValue !== '') {
      dispatch({
        type: ACTION_TYPES.updateSelectFieldValue,
        fieldValue: {
          id,
          value: defaultValue,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`SelectField relative border border-primary px-5 pt-3 rounded-2xl mt-4 md:mt-5 gfield gfield-${type} ${cssClass} ${
        visibility === 'hidden' ? 'hidden' : ''
      }`.trim()}
    >
      <label className="text-sm text-black font-medium" htmlFor={htmlId}>
        {label}
      </label>
      <FieldDecorator>
        <IonList>
          <IonItem>
            <IonSelect
              name={String(id)}
              id={htmlId}
              value={value}
              placeholder={placeholder}
              aria-label={label}
              interface="action-sheet"
              interfaceOptions={customActionSheetOptions}
              className="w-full border-0 text-black text-sm italic pl-0 focus:ring-0 focus:ring-opacity-0 focus:ring-transparent focus:border-primary"
              onIonChange={(ev) =>
                dispatch({
                  type: ACTION_TYPES.updateSelectFieldValue,
                  fieldValue: {
                    id,
                    value: ev.detail.value,
                  },
                })
              }
              multiple={false}
            >
              {options?.map((choice) => (
                <IonSelectOption
                  key={choice?.value || ''}
                  value={choice?.value || ''}
                >
                  {choice?.text || ''}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>
        </IonList>
      </FieldDecorator>
      <FieldDescription description={description} />
      {fieldErrors?.length
        ? fieldErrors.map((fieldError) => (
            <p
              key={fieldError.id}
              className="error-message text-error font-medium text-sm mt-2"
            >
              {fieldError.message}
            </p>
          ))
        : null}
    </div>
  );
}
